import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, Loader2, AlertCircle, CheckCircle2, Brain, TrendingUp, Heart } from 'lucide-react';
import { fetchAPI } from '../services/api';

const AIScriptMatcher = ({ onDownload, user }) => {
  const [script, setScript] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [userAccess, setUserAccess] = useState(user?.has_ai_access);

  // Polling para verificar se acesso foi liberado (a cada 2 segundos)
  useEffect(() => {
    if (!userAccess) {
      const interval = setInterval(async () => {
        try {
          const response = await fetchAPI('/auth/me', { method: 'GET' }, 'user');
          console.log('🔍 Verificando acesso IA:', response?.has_ai_access);
          if (response?.has_ai_access === true) {
            console.log('✅ Acesso liberado! Recarregando...');
            setUserAccess(true);
            // Força reload completo da página
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        } catch (err) {
          console.error('❌ Erro ao verificar acesso:', err);
        }
      }, 2000); // Verifica a cada 2 segundos

      return () => clearInterval(interval);
    }
  }, [userAccess]);

  // Verifica se usuário tem acesso (comprou produto 87 OU admin liberou manualmente)
  const hasPremiumAccess = userAccess === true;

  const handleAnalyze = async () => {
    if (script.trim().length < 50) {
      setError('Por favor, insira um roteiro com pelo menos 50 caracteres.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetchAPI('/ai/match-script', {
        method: 'POST',
        body: JSON.stringify({
          script: script,
          max_suggestions: 10,
          min_confidence: 0.6
        })
      }, 'user');

      setResults(response);
    } catch (err) {
      console.error('Erro na análise:', err);
      setError(
        err.message || 'Erro ao analisar roteiro. Tente novamente.'
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-400 bg-green-500/10 border-green-500/30';
    if (score >= 0.6) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
    return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
  };

  // Se não tem acesso premium, mostra paywall
  if (!hasPremiumAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          
          {/* Badge de verificação automática */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full mb-4">
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-purple-300 font-medium">
              Verificando acesso automaticamente...
            </span>
          </div>

          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            🤖 IA Matching Premium
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed">
            Desbloqueie o poder da Inteligência Artificial para sugerir cenas perfeitas baseadas no seu roteiro!
          </p>
          
          {/* Card de Benefícios */}
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 mb-6">
            <p className="text-sm text-gray-300 mb-4 font-bold">O que você ganha:</p>
            <ul className="text-left space-y-3 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>✨ <strong className="text-white">Análise inteligente de roteiro</strong> - IA analisa mood, tema e tipo de cena</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>🎯 <strong className="text-white">Sugestões precisas</strong> - Match semântico com 60%+ de confiança</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>⚡ <strong className="text-white">Economize horas</strong> - Encontre cenas perfeitas em segundos</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>💎 <strong className="text-white">Acesso vitalício</strong> - Pague uma vez, use para sempre</span>
              </li>
            </ul>
          </div>
          
          {/* CTA sem preço */}
          <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-2 border-purple-500/30 rounded-xl p-6 mb-6">
            <button
              onClick={() => window.open('https://pay.kiwify.com.br/seu_link_produto_87', '_blank')}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-lg font-bold text-lg mb-3"
            >
              🚀 Desbloquear IA Agora
            </button>
            <p className="text-xs text-gray-500">Acesso liberado automaticamente após o pagamento</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-full border border-purple-500/20">
            <Brain className="w-5 h-5 text-purple-400" />
            <span className="text-sm font-semibold text-purple-300">
              Powered by AI
            </span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-3 flex items-center justify-center gap-3">
            <Sparkles className="w-10 h-10 text-purple-400" />
            Matching de Cenas com IA
          </h1>
          
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Cole seu roteiro e nossa IA vai sugerir as melhores cenas da biblioteca 
            que combinam perfeitamente com sua narrativa.
          </p>
        </div>

        {/* Input Area */}
        <div className="bg-[#0D0D0D] rounded-2xl shadow-xl p-6 mb-8 border border-[#1A1A1A]">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-purple-400" />
            <h2 className="text-xl font-semibold text-white">Seu Roteiro</h2>
          </div>

          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Cole aqui o texto do seu roteiro, descrição do vídeo, ou briefing do projeto.

Exemplo:
'Vídeo institucional para empresa de tecnologia. Mostra equipe jovem e diversa trabalhando em ambiente moderno. Transições entre cenas de escritório, reuniões criativas e momentos de celebração. Tom inspirador e dinâmico.'

Quanto mais detalhes, melhor será a sugestão da IA!"
            className="w-full h-64 p-4 border-2 border-[#2A2A2A] rounded-xl bg-[#0A0A0A] text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none transition-colors resize-none font-mono text-sm"
            disabled={isAnalyzing}
          />

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              {script.length} caracteres {script.length >= 50 ? '✓' : `(mínimo 50)`}
            </div>

            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing || script.trim().length < 50}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-bold"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Analisar com IA
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400 mb-1">Erro na Análise</h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-6">
            
            {/* Analysis Summary */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-2xl p-6 border-2 border-purple-500/20">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <h2 className="text-2xl font-bold text-white">Análise Concluída</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Script Summary */}
                <div>
                  <h3 className="font-semibold text-gray-300 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Resumo do Roteiro
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {results.script_summary}
                  </p>
                </div>

                {/* Metadata */}
                <div className="space-y-3">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Mood</h4>
                    <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium border border-purple-500/30">
                      {results.mood}
                    </span>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Temas Detectados</h4>
                    <div className="flex flex-wrap gap-2">
                      {results.detected_themes.map((theme, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-xs font-medium border border-blue-500/30"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Tipos de Cena</h4>
                    <div className="flex flex-wrap gap-2">
                      {results.scene_types.map((type, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-green-500/20 text-green-300 rounded-lg text-xs font-medium border border-green-500/30"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-purple-500/20">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">
                    <strong className="text-white">{results.total_matches}</strong> cenas encontradas
                  </span>
                  <span className="text-gray-500">
                    Analisado em {new Date(results.analysis_timestamp).toLocaleTimeString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Matched Scenes */}
            {results.matches.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                  <h2 className="text-2xl font-bold text-white">
                    Cenas Recomendadas
                  </h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {results.matches.map((match, idx) => (
                    <div
                      key={match.pack_id}
                      className="bg-[#0D0D0D] rounded-xl shadow-lg border-2 border-[#1A1A1A] hover:border-purple-500/30 transition-all duration-200 overflow-hidden group hover:shadow-xl transform hover:-translate-y-1"
                    >
                      {/* Thumbnail */}
                      {match.thumbnail_url && (
                        <div className="relative aspect-video overflow-hidden bg-black">
                          <img
                            src={match.thumbnail_url}
                            alt={match.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute top-2 right-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getConfidenceColor(match.confidence_score)}`}>
                              {Math.round(match.confidence_score * 100)}% match
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="p-4">
                        {/* Title */}
                        <h3 className="font-bold text-white mb-2 line-clamp-2">
                          {match.title}
                        </h3>

                        {/* Collection */}
                        {match.collection && (
                          <span className="inline-block px-2 py-1 bg-[#1A1A1A] text-gray-400 rounded text-xs font-medium mb-3 border border-[#2A2A2A]">
                            {match.collection}
                          </span>
                        )}

                        {/* Relevance Reason */}
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Por que essa cena?</p>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {match.relevance_reason}
                          </p>
                        </div>

                        {/* Suggested Use */}
                        <div className="mb-4">
                          <p className="text-xs text-purple-400 mb-1 font-semibold">Sugestão de uso:</p>
                          <p className="text-sm text-gray-400 leading-relaxed">
                            {match.suggested_scene_description}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => onDownload && onDownload(match)}
                            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-center text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <Heart className="w-4 h-4" />
                            Baixar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-xl p-8 text-center">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                  Nenhuma cena encontrada
                </h3>
                <p className="text-yellow-300">
                  Não encontramos cenas que correspondessem ao seu roteiro. 
                  Tente ser mais descritivo ou ajustar o conteúdo.
                </p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AIScriptMatcher;
